import React, { useCallback } from "react";

import { SingleSelectInput, toast } from "@performyard/py-components";
import type { Option } from "@performyard/py-components/src/types/Option";

import { getWorkflows } from "../../../api/services/defs/workflows";

export type WorkflowValue = { id: string; name: string };

type SelectWorkflowProps = {
	value?: WorkflowValue | null;
	onChange: (workflow: WorkflowValue | null) => void;
	placeholder?: string;
	id?: string;
	"data-testid"?: string;
};

const SelectWorkflow = ({
	value,
	onChange,
	placeholder = "Search for a workflow...",
	id,
	"data-testid": dataTestId,
}: SelectWorkflowProps) => {
	const fetchWorkflowOptions = useCallback(async (searchTerm: string): Promise<Option<WorkflowValue>[]> => {
		try {
			const response = await getWorkflows({
				name: searchTerm || undefined,
				limit: 100,
				sortBy: "name",
				sortOrder: "asc",
			});
			return response.workflows.map((workflow) => ({
				// Many launched workflows share a template name, so include the
				// assignee's name to tell instances apart in the dropdown.
				label: workflow.subject ? `${workflow.name} — ${workflow.subject.name}` : workflow.name,
				value: { id: workflow._id, name: workflow.name },
			}));
		} catch {
			toast.error("There was an error loading workflows.");
			return [];
		}
	}, []);

	return (
		<SingleSelectInput<WorkflowValue>
			fetchOptions={fetchWorkflowOptions}
			value={value ?? undefined}
			onChange={onChange}
			selectionVariant="combobox"
			placeholder={placeholder}
			isClearable
			id={id}
			data-testid={dataTestId}
		/>
	);
};

export default SelectWorkflow;
